// src/components/dashboard/candidate/profile/journey-steps/HoursStep.tsx
import React from 'react';
import { Clock } from 'lucide-react';
import { CandidateProfileCreatePayload } from '@/lib/models/candidateProfile';

interface HoursStepProps {
    formData: Partial<CandidateProfileCreatePayload>;
    updateFormData: (updates: Partial<CandidateProfileCreatePayload>) => void;
}

export const HoursStep: React.FC<HoursStepProps> = ({ formData, updateFormData }) => {
    const minHours = formData.min_hours_per_week || 15;
    const maxHours = formData.max_hours_per_week || 25;

    const handleMinChange = (value: number) => {
        updateFormData({
            min_hours_per_week: value,
            // Ensure max is at least equal to min
            max_hours_per_week: Math.max(value, maxHours)
        });
    };

    const handleMaxChange = (value: number) => {
        updateFormData({
            max_hours_per_week: value,
            // Ensure min is at most equal to max
            min_hours_per_week: Math.min(value, minHours)
        });
    };

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-500">
            {/* Header */}
            <div className="text-center mb-8">
                <div className="w-16 h-16 mx-auto mb-4 bg-[#A4C3B2] rounded-2xl flex items-center justify-center">
                    <Clock className="w-8 h-8 text-[#1A4D4F]" />
                </div>
                <h2 className="text-2xl font-bold mb-2">Hur många timmar kan du arbeta?</h2>
                <p className="text-gray-600">
                    Vi matchar dig med jobb som passar ditt schema.<br />
                    Perfekt för att balansera studier och arbete!
                </p>
            </div>

            {/* Display current range */}
            <div className="text-center p-6 bg-[#A4C3B2]/30 rounded-2xl border-2 border-[#A4C3B2]">
                <div className="text-sm text-[#1A4D4F] font-medium mb-2">Din tillgänglighet</div>
                <div className="text-4xl font-bold text-[#212121]">
                    {minHours === maxHours ? (
                        <>{minHours} timmar/vecka</>
                    ) : (
                        <>{minHours}-{maxHours} timmar/vecka</>
                    )}
                </div>
            </div>

            {/* Min hours slider */}
            <div>
                <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-semibold text-gray-700">Minimum timmar/vecka</label>
                    <span className="text-2xl font-bold text-[#1A4D4F]">{minHours}</span>
                </div>
                <div className="relative h-6 flex items-center">
                    <div className="absolute left-0 right-0 h-2.5 bg-gray-200 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-[#1A4D4F] rounded-full transition-all duration-150"
                            style={{ width: `${((minHours - 1) / 39) * 100}%` }}
                        />
                    </div>
                    <input
                        type="range"
                        min="1"
                        max="40"
                        value={minHours}
                        onChange={(e) => handleMinChange(parseInt(e.target.value))}
                        className="hour-slider relative w-full appearance-none bg-transparent cursor-pointer z-10"
                    />
                </div>
                <div className="flex items-center justify-between mt-1.5 text-xs text-gray-400">
                    <span>1h</span>
                    <span>40h</span>
                </div>
            </div>

            {/* Max hours slider */}
            <div>
                <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-semibold text-gray-700">Maximum timmar/vecka</label>
                    <span className="text-2xl font-bold text-[#1A4D4F]">{maxHours}</span>
                </div>
                <div className="relative h-6 flex items-center">
                    <div className="absolute left-0 right-0 h-2.5 bg-gray-200 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-[#1A4D4F] rounded-full transition-all duration-150"
                            style={{ width: `${((maxHours - 1) / 39) * 100}%` }}
                        />
                    </div>
                    <input
                        type="range"
                        min="1"
                        max="40"
                        value={maxHours}
                        onChange={(e) => handleMaxChange(parseInt(e.target.value))}
                        className="hour-slider relative w-full appearance-none bg-transparent cursor-pointer z-10"
                    />
                </div>
                <div className="flex items-center justify-between mt-1.5 text-xs text-gray-400">
                    <span>1h</span>
                    <span>40h</span>
                </div>
            </div>

            {/* CSS for custom slider thumb */}
            <style>{`
                .hour-slider::-webkit-slider-thumb {
                    -webkit-appearance: none;
                    appearance: none;
                    width: 22px;
                    height: 22px;
                    margin-top: -6px;
                    background: #1A4D4F;
                    cursor: pointer;
                    border-radius: 50%;
                    border: 3px solid white;
                    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(26, 77, 79, 0.3);
                }
                .hour-slider::-webkit-slider-thumb:hover {
                    transform: scale(1.15);
                    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2), 0 3px 12px rgba(26, 77, 79, 0.4);
                }
                .hour-slider::-webkit-slider-thumb:active {
                    transform: scale(1.05);
                }
                .hour-slider::-moz-range-thumb {
                    width: 16px;
                    height: 16px;
                    background: #1A4D4F;
                    cursor: pointer;
                    border-radius: 50%;
                    border: 3px solid white;
                    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(26, 77, 79, 0.3);
                }
                .hour-slider::-webkit-slider-runnable-track {
                    background: transparent;
                    height: 10px;
                }
                .hour-slider::-moz-range-track {
                    background: transparent;
                    height: 10px;
                    border: none;
                }
            `}</style>
        </div>
    );
};